import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Ticket, Users, Euro, Download, Settings2, Search, Trash2, ArrowLeft, UserPlus, Utensils } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime, EUR2 } from "@/lib/format";
import { BOOKING_STATUS } from "@/lib/seminars";
import BookingConfigDialog from "@/components/seminars/BookingConfigDialog";
import ManualBookingDialog from "@/components/seminars/ManualBookingDialog";

const ALL = "__all";

function Kpi({ icon: Icon, label, value, testId }) {
  return (
    <Card className="p-4 border border-stone-200 rounded-xl shadow-none">
      <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5"><Icon size={12}/>{label}</div>
      <div className="text-2xl font-semibold mt-1" data-testid={testId}>{value}</div>
    </Card>
  );
}

function BookingRow({ b, onStatus, onDelete }) {
  const st = BOOKING_STATUS[b.status] || BOOKING_STATUS.nuova;
  return (
    <li className="px-4 py-3 flex flex-col lg:flex-row lg:items-center gap-3" data-testid={`booking-${b.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm">{b.last_name} {b.first_name}</span>
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
          <span className="text-[11px] text-stone-500">· {b.course_name}</span>
          {b.source === "manuale" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-600">manuale</span>}
        </div>
        <div className="text-[11px] text-stone-600 mt-0.5">
          {b.email ? <a href={`mailto:${b.email}`} className="hover:underline">{b.email}</a> : "senza email"} · {b.phone ? <a href={`tel:${b.phone}`} className="hover:underline">{b.phone}</a> : "senza telefono"}
          {b.church && ` · ${b.church}`} · {fmtDateTime(b.created_at)}
        </div>
        {(b.participant_names?.length > 0 || b.notes) && (
          <div className="text-[11px] text-stone-500 mt-0.5">{b.participant_names?.length > 0 && `Partecipanti: ${b.participant_names.join(", ")}`}{b.notes && ` · Note: ${b.notes}`}</div>
        )}
      </div>
      <div className="flex items-center gap-3">
        <div className="text-right">
          <div className="text-sm font-semibold">{EUR2.format(b.total_amount || 0)}</div>
          <div className="text-[11px] text-stone-500">{b.participants} pers.{b.lunches > 0 && <> · <Utensils size={10} className="inline"/> {b.lunches} pranzi</>}</div>
        </div>
        <Select value={b.status} onValueChange={(v) => onStatus(b, v)}>
          <SelectTrigger className="w-36 h-8 text-xs" data-testid={`booking-status-${b.id}`}><SelectValue/></SelectTrigger>
          <SelectContent>{Object.entries(BOOKING_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
        <button onClick={() => onDelete(b)} className="p-1.5 rounded hover:bg-red-50 text-stone-400 hover:text-red-700" data-testid={`booking-delete-${b.id}`}><Trash2 size={14}/></button>
      </div>
    </li>
  );
}

export default function SeminarBookings() {
  const qc = useQueryClient();
  const [courseId, setCourseId] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [q, setQ] = useState("");
  const [cfgOpen, setCfgOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const params = { course_id: courseId === ALL ? undefined : courseId, status: status === ALL ? undefined : status };
  const { data: seminars = [] } = useQuery({ queryKey: ["seminars"], queryFn: async () => (await api.get("/courses", { params: { is_seminar: true } })).data });
  const { data: bookings = [], isLoading } = useQuery({ queryKey: ["seminar-bookings", params], queryFn: async () => (await api.get("/seminar-bookings", { params })).data });
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? bookings.filter((b) => `${b.first_name} ${b.last_name} ${b.email} ${b.phone} ${b.church || ""}`.toLowerCase().includes(s)) : bookings;
  }, [bookings, q]);
  const active = filtered.filter((b) => b.status !== "annullata");
  const refresh = () => qc.invalidateQueries({ queryKey: ["seminar-bookings"] });
  const upd = useMutation({ mutationFn: async ({ id, status: st }) => (await api.patch(`/seminar-bookings/${id}`, { status: st })).data, onSuccess: () => { toast.success("Stato aggiornato"); refresh(); } });
  const del = useMutation({ mutationFn: async (id) => (await api.delete(`/seminar-bookings/${id}`)).data, onSuccess: () => { toast.success("Prenotazione eliminata"); refresh(); } });
  const exportXlsx = async () => {
    const res = await api.get("/seminar-bookings/export.xlsx", { params, responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(res.data);
    a.download = "iscrizioni-seminari.xlsx";
    a.click();
  };

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1300px] mx-auto">
      <Link to="/scuola/seminari" className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-[#1C352D] mb-4"><ArrowLeft size={13}/> Seminari</Link>
      <PageHeader
        title="Iscrizioni seminari"
        subtitle="Prenotazioni degli esterni dal catalogo online. Aggiorna lo stato quando confermi o ricevi il pagamento."
        actions={
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" className="rounded-full" onClick={() => setAddOpen(true)} data-testid="bookings-add-btn"><UserPlus size={14} className="mr-1.5"/> Aggiungi iscritto</Button>
            <Button variant="outline" className="rounded-full" onClick={() => setCfgOpen(true)} data-testid="bookings-config-btn"><Settings2 size={14} className="mr-1.5"/> Pagamento e notifiche</Button>
            <Button className="rounded-full bg-[#1C352D] hover:bg-[#264A3E]" onClick={exportXlsx} data-testid="bookings-export-btn"><Download size={14} className="mr-1.5"/> Esporta Excel</Button>
          </div>
        }
      />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <Kpi icon={Ticket} label="Prenotazioni" value={active.length} testId="bookings-kpi-count"/>
        <Kpi icon={Users} label="Persone" value={active.reduce((a, b) => a + b.participants, 0)} testId="bookings-kpi-people"/>
        <Kpi icon={Utensils} label="Pranzi" value={active.reduce((a, b) => a + (b.lunches || 0), 0)} testId="bookings-kpi-lunches"/>
        <Kpi icon={Euro} label="Da incassare" value={EUR2.format(active.filter((b) => b.status !== "pagata").reduce((a, b) => a + (b.total_amount || 0), 0))} testId="bookings-kpi-due"/>
        <Kpi icon={Euro} label="Incassato" value={EUR2.format(active.filter((b) => b.status === "pagata").reduce((a, b) => a + (b.total_amount || 0), 0))} testId="bookings-kpi-paid"/>
      </div>
      <div className="flex flex-col md:flex-row gap-3 mb-3">
        <Select value={courseId} onValueChange={setCourseId}>
          <SelectTrigger className="md:w-72" data-testid="bookings-filter-course"><SelectValue/></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Tutti i seminari</SelectItem>{seminars.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="md:w-44" data-testid="bookings-filter-status"><SelectValue/></SelectTrigger>
          <SelectContent><SelectItem value={ALL}>Tutti gli stati</SelectItem>{Object.entries(BOOKING_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
        <div className="relative md:ml-auto md:w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nome, email, telefono" className="pl-8" data-testid="bookings-search"/>
        </div>
      </div>
      <Card className="border border-stone-200 rounded-xl shadow-none overflow-hidden">
        {isLoading && <div className="p-8 text-center text-sm text-stone-400">Caricamento…</div>}
        {!isLoading && filtered.length === 0 && <div className="p-10 text-center text-sm text-stone-400">Nessuna prenotazione.</div>}
        <ul className="divide-y divide-stone-100">
          {filtered.map((b) => (
            <BookingRow key={b.id} b={b} onStatus={(x, st) => upd.mutate({ id: x.id, status: st })}
              onDelete={(x) => { if (window.confirm(`Eliminare la prenotazione di ${x.first_name} ${x.last_name}?`)) del.mutate(x.id); }}/>
          ))}
        </ul>
      </Card>
      <BookingConfigDialog open={cfgOpen} onOpenChange={setCfgOpen}/>
      <ManualBookingDialog open={addOpen} onOpenChange={setAddOpen} seminars={seminars}/>
    </div>
  );
}
